import { isOhioLihtcProgram } from '../AssetInformationForm/stateRules';
import { STATE_CODES } from '../stateCodes';
import { ALTERNATE_INCOME_ROW_NAMES } from './constants';

const EXCLUDED_INCOME_VERIFICATION_ROWS = [
  'collectChildSupport',
  'anticipateChildSupport',
  'publicHousing',
];

const EMPLOYMENT_TYPE_OPTIONS = [
  'Not Applicable',
  'Agriculture',
  'Business/Office',
  'FT Student, No Special Conditions',
  'Government/Public Service',
  'Homemaker',
  'Industrial/Manufacturing',
  'Not Employed',
  'Not Skilled/Unskilled',
  'Retired',
  'Self Employed',
  'Skilled/Specialized',
  'Technical/Professional',
].sort((a, b) =>
  a.localeCompare(b, 'en', { numeric: true, sensitivity: 'base' }),
);

const OTHER_INCOME_TYPE_OPTIONS = [
  'Adoption Assistance Payments',
  'Business',
  'Child Support',
  'Distributions from a Retirement or similar account',
  'Federal Wage',
  'General Assistance/Welfare',
  'Income From Assets Not Listed',
  'Indian Trust',
  'Military Pay',
  'Non-Federal Wage',
  'Other Non-Wage Source',
  'Pensions',
  'Safe Harbor Income Source',
  'Social Security',
  'Supplemental Security Income',
  'TANF',
  'Unemployment',
];

const INCOME_VERIFICATION_OPTIONS = [
  "Attorney's Statement",
  'Copy of Benefits/Payment Check',
  'Bank/Trustee Verification',
  'Accountant/CPA Statement',
  'Check Stubs/Earning Statement',
  'Employer Verification',
  'Not Verified',
  'Other',
  'Payer/Benefactor Affidavit',
  'Benefits Provider Verification',
  'Separation/Divorce Settlement',
  'Tax Returns - Business',
  'Tax Returns - Individuals',
].sort((a, b) =>
  a.localeCompare(b, 'en', { numeric: true, sensitivity: 'base' }),
);

export const INCOME_ALTERNATE_FORM_RULES_BY_STATE = {
  [STATE_CODES.OHIO]: {
    requiresHotmaAndLihtc: true,
    employmentTypeColumnLabel: 'Employment Type',
    annualGrossLabel: 'Annual Gross $ Amount',
    verificationHeaderLabel: 'Income Verification',
    selectEmploymentTypeLabel: 'Select Employment Type',
    selectIncomeTypeLabel: 'Select Income Type',
    selectIncomeVerificationLabel: 'Select Income Verification',
    requiresIncomeVerification: true,
    excludedIncomeVerificationRows: EXCLUDED_INCOME_VERIFICATION_ROWS,
    employmentTypeOptions: EMPLOYMENT_TYPE_OPTIONS,
    otherIncomeTypeOptions: OTHER_INCOME_TYPE_OPTIONS,
    incomeVerificationOptions: [...INCOME_VERIFICATION_OPTIONS],
  },
};

export const getIncomeAlternateFormRulesByState = (stateCode: ?string) =>
  INCOME_ALTERNATE_FORM_RULES_BY_STATE[stateCode] || null;

const normalizeStateCode = (stateCode: ?string): ?string => {
  if (!stateCode) return stateCode;
  const normalized = String(stateCode).toUpperCase();
  if (normalized === 'OH') return STATE_CODES.OHIO;
  return normalized;
};

/**
 * Whether this property `physicalState` has a dedicated income form layout/ruleset.
 * States with `requiresHotmaAndLihtc` (Ohio LIHTC today) use alternate columns only when HOTMA is active.
 * New states: add an entry to `INCOME_ALTERNATE_FORM_RULES_BY_STATE` — no container change.
 */
export const hasStateSpecificIncomeFormLayout = (
  stateCode: ?string,
  programName: ?string = '',
  _flags: Object = {},
  isHotmaActive: boolean = false,
): boolean => {
  const rules = getIncomeAlternateFormRulesByState(
    normalizeStateCode(stateCode),
  );
  if (!rules) {
    return false;
  }
  if (rules.requiresHotmaAndLihtc) {
    return isOhioLihtcProgram(programName) && isHotmaActive;
  }
  return true;
};

const incomeVerificationUnset = (v: mixed) =>
  v == null || v === '' || +v === -1;

/**
 * Ohio income form: align stored values with the Income Verification dropdown.
 * - Unset Income Verification fields are normalized to -1 (disabled placeholder).
 * - This keeps `renderSelectField` controlled and avoids browser auto-selecting the first option.
 */
export const applyOhioIncomeVerificationDefaults = (values: Object): Object => {
  const first =
    INCOME_ALTERNATE_FORM_RULES_BY_STATE[STATE_CODES.OHIO]
      ?.incomeVerificationOptions?.[0];
  if (!first) {
    return values;
  }
  const out = { ...values };
  ALTERNATE_INCOME_ROW_NAMES.forEach((name) => {
    if (EXCLUDED_INCOME_VERIFICATION_ROWS.includes(name)) {
      return;
    }
    const key = `${name}IncomeVerification`;
    if (!incomeVerificationUnset(out[key])) {
      return;
    }
    out[key] = -1;
  });
  return out;
};

export const OHIO_OTHER_INCOME_TYPE_UNSET = -1;

export const applyOhioOtherIncomeTypeDefaults = (values: Object): Object => {
  const out = { ...values };
  const allowed = new Set(
    INCOME_ALTERNATE_FORM_RULES_BY_STATE[STATE_CODES.OHIO]
      ?.otherIncomeTypeOptions || [],
  );
  if (!allowed.size) {
    return out;
  }
  const list = Array.isArray(out?.otherList) ? out.otherList : [];
  out.otherList = list.map((row) => {
    const fromDropdown =
      row?.IncomeType && allowed.has(row.IncomeType) ? row.IncomeType : null;
    // After save, Ohio submit stores the TIC label in IncomeTypeForTic and overwrites IncomeType with
    // EmploymentType (slug). The Other "Type of Income" field is still bound to IncomeType — recover
    // the saved label from IncomeTypeForTic when IncomeType is no longer a valid option.
    const fromTic =
      row?.IncomeTypeForTic && allowed.has(row.IncomeTypeForTic)
        ? row.IncomeTypeForTic
        : null;
    const incomeType = fromDropdown || fromTic;
    if (!incomeType) {
      return { ...row, IncomeType: OHIO_OTHER_INCOME_TYPE_UNSET };
    }
    if (row.IncomeType === incomeType) {
      return row;
    }
    return { ...row, IncomeType: incomeType };
  });
  return out;
};
